import ApplicationEntry from '@/application/components/sheet/ApplicationEntry'
import { ApplicationsSubscription } from '@gql'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getLatestVisitDate } from '@utils/getLatestVisitDate'
import { formatCurrency } from '@almaris/shared/utils/formatCurrency'
import { ApplicationStatusBadge } from '../ApplicationStatusBadge/ApplicationStatusBadge'
import { useApplicationDocumentEntries } from '../../hooks/useApplicationDocumentEntries'

type SellerContentProps = {
  application: ApplicationsSubscription['application'][number]
  netSeller: number
}

const SellerContent = ({ application, netSeller }: SellerContentProps) => {
  const { t } = useTranslation()
  const { otherDocumentsEntry, typedDocumentsEntries } =
    useApplicationDocumentEntries(application)

  const shouldHideBuyerContactInfo =
    application.estate.seller.hideBuyerContactInfo

  const contributionPercent = useMemo(() => {
    if (!application) return 0
    const { offerAmount, contribution } = application
    if (!offerAmount || !contribution) return 0
    return (contribution / offerAmount) * 100
  }, [application])

  const visitDate = useMemo(() => {
    const latestVisitDate = getLatestVisitDate(application.buyer_member.visits)
    if (latestVisitDate == null) return t('ApplicationSheet.notDefined')

    return format(latestVisitDate, 'dd MMMM yyyy HH:mm', { locale: fr })
  }, [application.buyer_member.visits, t])

  if (!application) return null

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <ApplicationEntry
        title={t('ApplicationsTableColumns.status')}
        value={<ApplicationStatusBadge application={application} />}
      />
      <ApplicationEntry
        title={t('ApplicationSheet.totAmount')}
        value={formatCurrency(application.offerAmount ?? 0)}
      />
      <ApplicationEntry
        title={t('ApplicationSheet.netSeller')}
        value={formatCurrency(netSeller)}
      />

      <ApplicationEntry
        title={t('ApplicationsTableColumns.visitDate')}
        value={visitDate}
      />
      <ApplicationEntry
        title={t('ApplicationSheet.offerDate')}
        value={
          application.submitDate
            ? format(new Date(application.submitDate), 'dd MMMM yyyy HH:mm', {
                locale: fr,
              })
            : t('ApplicationSheet.notDefined')
        }
      />
      <ApplicationEntry
        title={t('ApplicationSheet.contribution')}
        value={contributionPercent.toFixed(2) + '%'}
      />
      <ApplicationEntry
        title={t('ApplicationSheet.loanAmount')}
        value={formatCurrency(application.loan ?? 0)}
      />
      {!shouldHideBuyerContactInfo && (
        <>
          <ApplicationEntry
            title={otherDocumentsEntry.title}
            value={otherDocumentsEntry.value}
          />
          {typedDocumentsEntries.map((entry) => (
            <ApplicationEntry
              key={entry.title}
              title={entry.title}
              value={entry.value}
            />
          ))}
        </>
      )}
      <ApplicationEntry
        title={t('ApplicationSheet.comment')}
        value={application.comment ?? t('ApplicationSheet.notDefined')}
      />
    </div>
  )
}

export default SellerContent
